import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardActionArea,
  Tabs,
  Tab,
  IconButton,
  Button,
  ButtonGroup,
  useMediaQuery,
  useTheme,
  CardHeader,
  Alert,
  Menu,
  MenuItem,
  Chip,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from '@mui/material';
import School from '@mui/icons-material/School';
import Add from '@mui/icons-material/Add';
import Share from '@mui/icons-material/Share';
import MoreVert from '@mui/icons-material/MoreVert';
import ViewModule from '@mui/icons-material/ViewModule';
import TableChart from '@mui/icons-material/TableChart';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

import {
  useGetRubricsByOwnerQuery,
  useGetSharedRubricsQuery,
  useDeleteRubricMutation,
  useDuplicateRubricMutation,
} from './rubricsApi';
import CreateRubricWizard from './components/CreateRubricWizard';
import { setCurrentTab, setViewMode } from './rubricsSlice';

import type { Rubric, RubricContent } from '@/shared/types/rubric';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';
import ShareDialog from '@/shared/components/ShareDialog';
import { useConfirm } from '@/shared/components/ConfirmProvider';
import { PageSpinner } from '@/shared/components/PageSpinner';

function rubricTypeBadge(content: RubricContent | null): string {
  if (!content) return 'Empty';
  switch (content.rubricType) {
    case 'analytic':
      return 'Analytic';
    case 'holistic':
      return 'Holistic';
    case 'single-point':
      return 'Single-Point';
    default:
      return 'Unknown';
  }
}

function rubricTypeColor(
  content: RubricContent | null
): 'primary' | 'secondary' | 'success' | 'default' {
  if (!content) return 'default';
  switch (content.rubricType) {
    case 'analytic':
      return 'primary';
    case 'holistic':
      return 'secondary';
    case 'single-point':
      return 'success';
    default:
      return 'default';
  }
}

const MyRubrics: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [wizardOpen, setWizardOpen] = useState(false);
  const [shareRubricId, setShareRubricId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRubric, setMenuRubric] = useState<Rubric | null>(null);

  const navigate = useNavigate();
  const confirm = useConfirm();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const { viewMode, currentTab } = useAppSelector((s) => s.rubrics);
  const userId = user?.id ?? '';

  const {
    data: myRubrics = [],
    isLoading: loadingMine,
    error: errorMine,
  } = useGetRubricsByOwnerQuery(userId, { skip: !userId });
  const { data: sharedRubrics = [], isLoading: loadingShared } = useGetSharedRubricsQuery(userId, {
    skip: !userId,
  });
  const [deleteRubric] = useDeleteRubricMutation();
  const [duplicateRubric] = useDuplicateRubricMutation();

  const shareRubric = useMemo(() => {
    if (!shareRubricId) return null;
    return [...myRubrics, ...sharedRubrics].find((r) => r.id === shareRubricId) ?? null;
  }, [shareRubricId, myRubrics, sharedRubrics]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    dispatch(setCurrentTab(newValue));
  };

  const handleViewModeChange = (mode: 'grid' | 'table') => {
    dispatch(setViewMode(mode));
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, rubric: Rubric) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuRubric(rubric);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRubric(null);
  };

  const handleEdit = () => {
    if (menuRubric) navigate(`/curriculum/rubrics/${menuRubric.id}`);
    closeMenu();
  };

  const handleDuplicate = async () => {
    if (menuRubric) await duplicateRubric(menuRubric.id);
    closeMenu();
  };

  const handleShare = () => {
    if (menuRubric) setShareRubricId(menuRubric.id);
    closeMenu();
  };

  const handleDelete = async () => {
    if (!menuRubric) return;
    closeMenu();
    const confirmed = await confirm({
      title: 'Delete Rubric',
      message: `Are you sure you want to delete "${menuRubric.title}"? This cannot be undone.`,
      confirmButtonText: 'Delete',
    });
    if (confirmed) await deleteRubric(menuRubric.id);
  };

  const RubricCard: React.FC<{ rubric: Rubric }> = ({ rubric }) => (
    <Card sx={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea
        onClick={() => navigate(`/curriculum/rubrics/${rubric.id}`)}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardHeader
          title={rubric.title}
          action={
            <IconButton
              size="small"
              sx={{ color: 'primary.contrastText' }}
              onClick={(e) => openMenu(e, rubric)}
            >
              <MoreVert />
            </IconButton>
          }
          sx={{
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiCardHeader-title': { fontSize: '1.25rem' },
          }}
        />
        <Box sx={{ flexGrow: 1, px: 2, pt: 1.5, pb: 1, overflow: 'hidden' }}>
          <Chip
            label={rubricTypeBadge(rubric.content)}
            color={rubricTypeColor(rubric.content)}
            size="small"
            sx={{ mb: 1 }}
          />
          {rubric.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {rubric.description}
            </Typography>
          )}
        </Box>
        <Box
          component="footer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {new Date(rubric.updatedAt).toLocaleDateString()}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );

  const renderRubricContent = (rubrics: Rubric[], isLoading: boolean) => {
    if (isLoading) {
      return <PageSpinner />;
    }
    if (rubrics.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No rubrics found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentTab === 0
              ? 'Create your first rubric to get started'
              : 'No rubrics have been shared with you yet'}
          </Typography>
          {currentTab === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ mt: 2 }}
              onClick={() => setWizardOpen(true)}
            >
              Create New Rubric
            </Button>
          )}
        </Box>
      );
    }

    if (viewMode === 'table') {
      return (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell>Title</TableCell>
                {!isMobile && <TableCell>Type</TableCell>}
                {!isMobile && <TableCell>Description</TableCell>}
                {!isMobile && <TableCell>Last Modified</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rubrics.map((rubric) => (
                <TableRow
                  key={rubric.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/curriculum/rubrics/${rubric.id}`)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {rubric.title}
                      </Typography>
                      {isMobile && (
                        <Chip
                          label={rubricTypeBadge(rubric.content)}
                          color={rubricTypeColor(rubric.content)}
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Chip
                        label={rubricTypeBadge(rubric.content)}
                        color={rubricTypeColor(rubric.content)}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell sx={{ maxWidth: 300 }}>
                      {rubric.description || (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.disabled"
                          sx={{ fontStyle: 'italic' }}
                        >
                          No description
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(rubric.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="More actions">
                      <IconButton size="small" onClick={(e) => openMenu(e, rubric)}>
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 3,
        }}
      >
        {rubrics.map((rubric) => (
          <RubricCard key={rubric.id} rubric={rubric} />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        {!!errorMine && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load rubrics
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
          <Typography variant="h2" component="h1">
            My Rubrics
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {!isMobile && (
              <ButtonGroup variant="outlined" size="small">
                <Button
                  onClick={() => handleViewModeChange('grid')}
                  variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                >
                  <ViewModule />
                </Button>
                <Button
                  onClick={() => handleViewModeChange('table')}
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                >
                  <TableChart />
                </Button>
              </ButtonGroup>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={() => setWizardOpen(true)}>
              {isMobile ? 'Create' : 'Create Rubric'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              icon={<School />}
              label="My Rubrics"
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<Share />}
              label="Shared with Me"
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        <Box>
          {currentTab === 0 && renderRubricContent(myRubrics, loadingMine)}
          {currentTab === 1 && renderRubricContent(sharedRubrics, loadingShared)}
        </Box>
      </Container>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        <MenuItem onClick={handleShare}>
          <Share fontSize="small" sx={{ mr: 1 }} /> Share
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Share dialog */}
      {shareRubric && (
        <ShareDialog
          open={Boolean(shareRubricId)}
          onClose={() => setShareRubricId(null)}
          objectId={shareRubric.id}
          objectType="rubric"
          ownerId={shareRubric.ownerId}
          shareUrl={`${window.location.origin}/curriculum/rubrics/${shareRubric.id}`}
        />
      )}

      {/* Creation wizard */}
      <CreateRubricWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </Box>
  );
};

export default MyRubrics;
